// src/features/testimonial/TestimonialSlider.tsx
'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { FaStar, FaCircleCheck, FaQuoteRight } from 'react-icons/fa6';
import 'swiper/css';
import 'swiper/css/navigation';

import NextImage from '../Image';
import { TestimonialProduct, TestimonialProductData } from './TestimonialProduct';

interface Testimonial {
  id: string | number;
  comment: string;
  avatar: string;
  rating: number;
  user_name: string;
  product: TestimonialProductData | null;
}

interface SliderProps {
  testimonials: Testimonial[];
}

const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false); 
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      const hasOverflow = element.scrollHeight > element.clientHeight;
      setIsTruncated(hasOverflow);
    }
  }, [testimonial.comment]); 

  return (
    <div className={`testimonial__item testimonial__item--premium ${isExpanded ? 'is-expanded' : ''}`}>
      <div className="testimonial__item-inner">
        
        {/* Header remains the same */}
        <div className="testimonial__header">
          <div className="testimonial__author-box">
            <div className="testimonial__author-thumb">
              <NextImage
                fallbackSrc="/images/icon/demo-person.jpg"
                src={testimonial.avatar || '/images/icon/demo-person.jpg'}
                alt={testimonial.user_name}
                width={48}
                height={48}
              />
            </div>
            <div className="testimonial__author-info">
              <h6>{testimonial.user_name}</h6>
            </div>
          </div>
          <div className="testimonial__rating">
            <div className="stars">
              {[...Array(5)].map((_, i) => 
              (<FaStar key={i} style={{ color: i < testimonial.rating ? '#ffc107' : '#e4e5e9', fontSize: '14px' }} />)
              )}
            </div>
          </div>
        </div>

        <div className="testimonial__body">
          <div className="quote-icon">
            <FaQuoteRight />
          </div>
          
          <div className={`comment-wrapper ${isExpanded ? 'expanded' : 'clamped'}`}>
            <p ref={textRef}>{testimonial.comment}</p>
          </div>
          
          {(isTruncated || isExpanded) && (
            <button 
              className="see-more" 
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Show Less' : 'See More'}
            </button>
          )}
        </div>


        {testimonial.product && (
          <div className="testimonial__footer">
            <TestimonialProduct product={testimonial.product} />
          </div>
        )}
        
      </div>
    </div>
  );
};



export const TestimonialSlider: React.FC<SliderProps> = ({ testimonials }) => {
  return (
    <div className="testimonial__wrapper">
      <Swiper
        modules={[Navigation, Autoplay]}
        navigation={{
          nextEl: '.testimonial-next',
          prevEl: '.testimonial-prev',
        }}
        loop={true}
        spaceBetween={30}
        breakpoints={{
          375: { slidesPerView: 1 },
          576: { slidesPerView: 1.5 },
          768: { slidesPerView: 2 },
          992: { slidesPerView: 2.5 },
          1200: { slidesPerView: 3 },
        }}
        className="testimonial__slider"
      >
        {testimonials.map((testimonial) => (
          <SwiperSlide key={testimonial.id}>
            <TestimonialCard testimonial={testimonial} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};